import { useState, type CSSProperties, type ReactNode } from 'react'

/**
 * A ranked list of shares — the top N, with the rest one click away.
 *
 * 🔴 IT REPLACED A FULL TABLE OF EVERY CLASS ON THE DASHBOARD. E1's class table
 * is eighteen rows of six columns, and a head teacher opening the page wants
 * one thing from it: which classes are quiet. Eighteen rows do not answer that
 * — they have to be read. Five ranked bars answer it before anything is read,
 * and the table is still there, further down, for the reader who then wants the
 * detail.
 *
 * 🔴 IT IS NOT `ShareBars` WITH A `slice(0, 5)`. That component draws whatever
 * it is handed, which means every caller decides its own cut-off and no caller
 * says what it dropped. `DashCharts`' own header is explicit that a silent
 * truncation reads as "this is all of it" — so the cut-off lives here, once,
 * and the control that undoes it is drawn beside the bars rather than left to
 * each screen.
 *
 * ⚠️ EXPANDING IS LOCAL STATE AND NEVER A FETCH. The rows are already in the
 * payload the screen holds; "View all" is a disclosure, not a page.
 */

export type RankedTone = 'primary' | 'leaf' | 'teal' | 'indigo' | 'sunshine' | 'coral'

const TONE: Record<RankedTone, string> = {
  primary: 'var(--color-primary)',
  leaf: 'var(--color-leaf)',
  teal: 'var(--color-teal)',
  indigo: 'var(--color-indigo)',
  sunshine: 'var(--color-sunshine)',
  coral: 'var(--color-coral)',
}

/** The warm neutral every empty track in the product uses. */
const TRACK = 'var(--color-pill-neutral-bg)'

export interface RankedRow {
  key: string
  label: ReactNode
  /** Drawn under the label — a teacher's name, a section, whatever dates it. */
  sub?: ReactNode
  count: number
  /** The denominator. Zero draws an empty track rather than dividing by it. */
  total: number
  tone?: RankedTone
  /** Opens the row. Omitted rather than drawn inert. */
  onOpen?: () => void
}

export interface RankedBarsProps {
  rows: readonly RankedRow[]
  /** How many to draw before the disclosure. */
  top?: number
  emptyMessage: ReactNode
  /** Localised. `(hidden) => \`View all 13 more\``. */
  moreLabel: (hidden: number) => ReactNode
  fewerLabel: ReactNode
}

export function RankedBars({
  rows,
  top = 5,
  emptyMessage,
  moreLabel,
  fewerLabel,
}: RankedBarsProps) {
  const [open, setOpen] = useState(false)

  if (rows.length === 0) {
    return (
      <p className="font-dash text-dash-xs font-semibold text-muted">{emptyMessage}</p>
    )
  }

  const shown = open ? rows : rows.slice(0, top)
  const hidden = rows.length - shown.length

  return (
    <div className="flex flex-col gap-4">
      <ul className="flex flex-col gap-3.5">
        {shown.map((row, i) => {
          const share = row.total > 0 ? Math.min(1, row.count / row.total) : 0
          const fill = TONE[row.tone ?? 'primary']
          const body = (
            <>
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate text-left font-dash text-dash-xs font-bold text-ink">
                  {row.label}
                  {row.sub !== undefined && (
                    <span className="ml-2 font-normal text-muted">{row.sub}</span>
                  )}
                </span>
                {/*
                  🔴 THE PAIR, ALWAYS — OD-39/OD-72 drawn. A bar at a third of
                  its track means nothing without "9 / 27" beside it, and a head
                  teacher reads these figures out at a governors' meeting where
                  a width cannot be read aloud.
                */}
                <span className="tabular shrink-0 font-dash text-dash-xs font-extrabold text-ink">
                  {row.count} / {row.total}
                </span>
              </div>
              <div
                aria-hidden="true"
                className="mt-1.5 h-2.5 w-full overflow-hidden rounded-full"
                style={{ background: TRACK }}
              >
                <div
                  className="play-rise h-full rounded-full"
                  style={
                    {
                      width: `${String(share * 100)}%`,
                      background: fill,
                      '--rise-i': i,
                    } as CSSProperties
                  }
                />
              </div>
            </>
          )

          return (
            <li key={row.key}>
              {row.onOpen ? (
                <button
                  type="button"
                  onClick={row.onOpen}
                  className="block w-full cursor-pointer rounded-md px-1 py-0.5 transition-colors duration-150 ease-dash hover:bg-canvas"
                >
                  {body}
                </button>
              ) : (
                <div className="px-1 py-0.5">{body}</div>
              )}
            </li>
          )
        })}
      </ul>

      {/*
        ⚠️ THE CONTROL IS ABSENT WHEN THERE IS NOTHING BEHIND IT. A school with
        four classes gets four bars and no "View all", rather than a button that
        expands a list to the same length.
      */}
      {(hidden > 0 || open) && (
        <button
          type="button"
          onClick={() => {
            setOpen((prev) => !prev)
          }}
          aria-expanded={open}
          className="self-start font-dash text-dash-xs font-bold text-primary transition-colors duration-150 ease-dash hover:text-primary-deep"
        >
          {open ? fewerLabel : moreLabel(hidden)}
        </button>
      )}
    </div>
  )
}
